import {apiClient} from 'core/apiClient';
import {
    Appeal,
    AppealAssignRequest,
    AppealCard,
    AppealExportFormat,
    AppealTheme,
    AppealsRegistryRequest,
    AppealsRegistryResponse,
    NkBranch,
    NkBranchRequest,
    NkAppealCreateRequest,
    NkEmployee,
    NkEmployeeCreateRequest,
    NkEmployeeUpdateRequest,
} from 'types';

const APPEALS_URL = 'service/appeals';
const NK_URL = 'service/nk';

export const fetchAppealsRegistry = async (
    request: AppealsRegistryRequest,
): Promise<AppealsRegistryResponse> =>
    (await apiClient.post<AppealsRegistryResponse>(`${APPEALS_URL}/page`, request)).data;

export const fetchNkEmployees = async (): Promise<NkEmployee[]> =>
    (await apiClient.get<NkEmployee[]>(`${NK_URL}/employees`)).data;

export const createNkEmployee = async (request: NkEmployeeCreateRequest): Promise<number> =>
    (await apiClient.post<number>(`${NK_URL}/employees`, request)).data;

export const updateNkEmployee = async ({
    id,
    request,
}: {
    id: number;
    request: NkEmployeeUpdateRequest;
}): Promise<void> => {
    await apiClient.put(`${NK_URL}/employees/${id}`, request);
};

export const blockNkEmployee = async ({
    id,
    reason,
}: {
    id: number;
    reason: string;
}): Promise<void> => {
    await apiClient.post(`${NK_URL}/employees/${id}/block`, undefined, {params: {reason}});
};

export const unblockNkEmployee = async ({
    id,
    reason,
}: {
    id: number;
    reason: string;
}): Promise<void> => {
    await apiClient.post(`${NK_URL}/employees/${id}/unblock`, undefined, {params: {reason}});
};

export const fetchNkBranches = async (): Promise<NkBranch[]> =>
    (await apiClient.get<NkBranch[]>(`${NK_URL}/branches`)).data;

export const fetchNkBranch = async (id: number): Promise<NkBranch> =>
    (await apiClient.get<NkBranch>(`${NK_URL}/branches/${id}`)).data;

export const createNkBranch = async (request: NkBranchRequest): Promise<number> =>
    (await apiClient.post<number>(`${NK_URL}/branches`, request)).data;

export const updateNkBranch = async ({
    id,
    request,
}: {
    id: number;
    request: NkBranchRequest;
}): Promise<void> => {
    await apiClient.put(`${NK_URL}/branches/${id}`, request);
};

export const deleteNkBranch = async (id: number): Promise<void> => {
    await apiClient.delete(`${NK_URL}/branches/${id}`);
};

export const blockNkBranch = async (id: number): Promise<void> => {
    await apiClient.post(`${NK_URL}/branches/${id}/block`);
};

export const unblockNkBranch = async (id: number): Promise<void> => {
    await apiClient.post(`${NK_URL}/branches/${id}/unblock`);
};

export const fetchAppealThemes = async (): Promise<AppealTheme[]> =>
    (await apiClient.get<AppealTheme[]>(`${APPEALS_URL}/dictionary`)).data;

export const exportAppealsRegistry = async ({
    request,
    format,
}: {
    request: AppealsRegistryRequest;
    format: AppealExportFormat;
}): Promise<Blob> =>
    (
        await apiClient.post(`${APPEALS_URL}/export`, request, {
            params: {format},
            responseType: 'blob',
        })
    ).data;

export const fetchAppealCard = async (id: number): Promise<AppealCard> =>
    (await apiClient.get<AppealCard>(`${APPEALS_URL}/${id}`)).data;

export const fetchAppealAct = async (id: number): Promise<Blob> =>
    (
        await apiClient.get(`${APPEALS_URL}/${id}/act`, {
            responseType: 'blob',
        })
    ).data;

export const fetchAppealPhoto = async ({
    id,
    photoId,
}: {
    id: number;
    photoId: number;
}): Promise<Blob> =>
    (
        await apiClient.get(`${APPEALS_URL}/${id}/photos/${photoId}`, {
            responseType: 'blob',
        })
    ).data;

export const takeAppeal = async ({id, theme}: {id: number; theme: string}): Promise<Appeal> =>
    (await apiClient.post<Appeal>(`${APPEALS_URL}/${id}/take`, null, {params: {theme}})).data;

export const resolveAppeal = async ({
    id,
    response,
}: {
    id: number;
    response: string;
}): Promise<Appeal> =>
    (await apiClient.post<Appeal>(`${APPEALS_URL}/${id}/resolve`, {response})).data;

export const rejectAppeal = async ({
    id,
    response,
}: {
    id: number;
    response: string;
}): Promise<Appeal> =>
    (await apiClient.post<Appeal>(`${APPEALS_URL}/${id}/reject`, {response})).data;

export const assignAppeal = async ({
    id,
    request,
}: {
    id: number;
    request: AppealAssignRequest;
}): Promise<Appeal> => (await apiClient.post<Appeal>(`${APPEALS_URL}/${id}/assign`, request)).data;

export const uploadAppealResponsePhotos = async ({
    id,
    files,
}: {
    id: number;
    files: File[];
}): Promise<number> => {
    const data = new FormData();
    files.forEach((file) => data.append('files', file));

    return (
        await apiClient.post<number>(`${APPEALS_URL}/${id}/response-photos`, data, {
            headers: {'Content-Type': 'multipart/form-data'},
        })
    ).data;
};

export const createNkAppeal = async (request: NkAppealCreateRequest): Promise<number> => {
    const data = new FormData();

    Object.entries(request).forEach(([key, value]) => {
        if (value == null || value === '') return;
        if (key === 'files' && Array.isArray(value)) {
            value.forEach((file) => data.append('files', file));
            return;
        }
        data.append(key, String(value));
    });

    return (await apiClient.post<number>(`${APPEALS_URL}/nk`, data)).data;
};
